import { dollarsToSatoshis, satoshisToDollars } from "../utils";
/** inventory_packages can be added to many of our main data types — such as: insts, products, deals, etc. */
export var InventoryType;
(function (InventoryType) {
    InventoryType["PUBLISHER_GROUPS"] = "PUB_GROUP";
    InventoryType["PMP_DEALS_AND_SUPERTAGS"] = "DEALS";
    InventoryType["LINE_ITEM_PUB_GROUP"] = "LINE_ITEM_PUB_GROUP";
})(InventoryType || (InventoryType = {}));
export var InventoryTypeV2;
(function (InventoryTypeV2) {
    InventoryTypeV2["MADHIVE_PRIVATE"] = "MADHIVE_PRIVATE";
    InventoryTypeV2["MADHIVE_PUBLIC"] = "MADHIVE_PUBLIC";
    InventoryTypeV2["SELF_SOURCED"] = "SELF_SOURCED";
    InventoryTypeV2["PARTNER_O_AND_O"] = "PARTNER_O_AND_O";
    InventoryTypeV2["PRIVATE_O_AND_O"] = "PRIVATE_O_AND_O";
})(InventoryTypeV2 || (InventoryTypeV2 = {}));
// Common text to display for the InventoryType options
export var InventoryTypeText;
(function (InventoryTypeText) {
    InventoryTypeText["DEALS"] = "PMP Deals & SuperTags";
    InventoryTypeText["LINE_ITEM_PUB_GROUP"] = "Line Item Publisher Group";
    InventoryTypeText["PUB_GROUP"] = "Publisher Group";
})(InventoryTypeText || (InventoryTypeText = {}));
// TODO combine InventoryContentType and ContentType https://madhive.atlassian.net/browse/INPACK-1190
export var InventoryContentType;
(function (InventoryContentType) {
    InventoryContentType["GENRE"] = "Genre";
    InventoryContentType["LANGUAGE"] = "Language";
})(InventoryContentType || (InventoryContentType = {}));
export var TargetingLogic;
(function (TargetingLogic) {
    TargetingLogic["AND"] = "AND";
    TargetingLogic["OR"] = "OR";
})(TargetingLogic || (TargetingLogic = {}));
/**
 * Per-package Genre selection mode:
 * INCLUDE — target genre ∈ `genres[]` (default; today's behavior).
 * EXCLUDE — target genre ∉ `genres[]` (all other genres match).
 * Independent of `TargetingLogic` (Genre-axis property; AND/OR still
 * combines Genre-axis against Language-axis).
 */
export var GenreSelectionMode;
(function (GenreSelectionMode) {
    GenreSelectionMode["INCLUDE"] = "include";
    GenreSelectionMode["EXCLUDE"] = "exclude";
})(GenreSelectionMode || (GenreSelectionMode = {}));
/**
 * Convert a ServiceInventoryPackages to InventoryPackages.
 * @param inventory_packages The ServiceInventoryPackages to convert.
 * @returns The InventoryPackages.
 */
export const serviceToInventoryPackages = (inventory_packages) => (inventory_packages || []).map((inventory_package) => {
    let contentType;
    if (inventory_package.genres?.length) {
        contentType = InventoryContentType.GENRE;
    }
    else if (inventory_package.languages?.length) {
        contentType = InventoryContentType.LANGUAGE;
    }
    return {
        id: inventory_package?.id,
        wlistId: inventory_package?.wlist_id,
        inventoryIds: inventory_package.inventory_ids,
        genres: inventory_package?.genres,
        languages: inventory_package?.languages,
        volumeCapPct: inventory_package?.volume_cap_pct
            ? inventory_package?.volume_cap_pct / 100
            : undefined,
        maxEcpm: inventory_package?.max_ecpm,
        priority: inventory_package?.priority,
        name: inventory_package.name,
        contentType,
        inventoryType: inventory_package?.inventory_package_type,
        publisherIds: inventory_package?.publisher_ids,
        ...(inventory_package?.targeting_logic !== undefined && {
            targetingLogic: inventory_package.targeting_logic
        }),
        ...(inventory_package?.genre_selection_mode !== undefined && {
            genreSelectionMode: inventory_package.genre_selection_mode
        })
    };
});
/**
 * Convert a ServiceInventoryPackages V2 to InventoryPackages V2.
 * @param inventory_packages The ServiceInventoryPackages V2 to convert.
 * @returns The InventoryPackages V2.
 */
export const serviceToInventoryPackagesV2 = (inventory_packages) => (inventory_packages || []).map((inventory_package) => {
    const contentType = inventory_package.genres?.length
        ? InventoryContentType.GENRE
        : inventory_package.languages?.length
            ? InventoryContentType.LANGUAGE
            : undefined;
    return {
        id: inventory_package?.id,
        inventoryIds: inventory_package.inventory_ids,
        genres: inventory_package?.genres,
        languages: inventory_package?.languages,
        volumeCapPct: inventory_package?.volume_cap_pct
            ? inventory_package?.volume_cap_pct / 100
            : undefined,
        priority: inventory_package?.priority,
        name: inventory_package.name,
        contentType,
        inventoryType: inventory_package?.supply_type,
        publisherIds: inventory_package?.publisher_ids,
        //V2
        pubGroupId: inventory_package?.pub_group_id?.length
            ? inventory_package?.pub_group_id
            : undefined,
        baseMediaFeeCpm: satoshisToDollars(inventory_package?.base_media_fee_cpm || 0),
        subGenres: inventory_package?.sub_genres,
        audienceWaterfallEnabled: !!inventory_package?.audience_waterfall_enabled,
        ...(inventory_package?.targeting_logic !== undefined && {
            targetingLogic: inventory_package.targeting_logic
        }),
        ...(inventory_package?.genre_selection_mode !== undefined && {
            genreSelectionMode: inventory_package.genre_selection_mode
        })
    };
});
/**
 * Convert an InventoryPackages to ServiceInventoryPackages.
 * @param inventoryPackages The InventoryPackages to convert.
 * @returns The ServiceInventoryPackages.
 */
export const inventoryPackagesToService = (inventoryPackages) => (inventoryPackages || []).map((inventoryPackage) => {
    return {
        id: inventoryPackage.id,
        name: inventoryPackage.name,
        inventory_package_type: inventoryPackage.inventoryType || InventoryType.PUBLISHER_GROUPS,
        wlist_id: inventoryPackage.wlistId,
        inventory_ids: inventoryPackage.inventoryIds,
        genres: inventoryPackage.genres,
        languages: inventoryPackage.languages,
        volume_cap_pct: inventoryPackage.volumeCapPct
            ? Math.round(inventoryPackage.volumeCapPct * 100)
            : undefined,
        max_ecpm: inventoryPackage.maxEcpm,
        priority: inventoryPackage.priority,
        publisher_ids: inventoryPackage.publisherIds,
        ...(inventoryPackage.targetingLogic !== undefined && {
            targeting_logic: inventoryPackage.targetingLogic
        }),
        ...(inventoryPackage.genreSelectionMode !== undefined && {
            genre_selection_mode: inventoryPackage.genreSelectionMode
        })
    };
});
/**
 * Convert an InventoryPackages V2 to ServiceInventoryPackages V2.
 * @param inventoryPackages The InventoryPackages V2 to convert.
 * @returns The ServiceInventoryPackages V2.
 */
export const inventoryPackagesToServiceV2 = (inventoryPackages) => (inventoryPackages || []).map((inventoryPackage) => {
    return {
        id: inventoryPackage.id,
        name: inventoryPackage.name,
        supply_type: inventoryPackage.inventoryType,
        inventory_ids: inventoryPackage.inventoryIds,
        genres: inventoryPackage.genres,
        languages: inventoryPackage.languages,
        volume_cap_pct: inventoryPackage.volumeCapPct
            ? Math.round(inventoryPackage.volumeCapPct * 100)
            : undefined,
        priority: inventoryPackage.priority,
        publisher_ids: inventoryPackage.publisherIds,
        pub_group_id: inventoryPackage.pubGroupId,
        base_media_fee_cpm: dollarsToSatoshis(inventoryPackage.baseMediaFeeCpm),
        sub_genres: inventoryPackage.subGenres,
        audience_waterfall_enabled: inventoryPackage.audienceWaterfallEnabled,
        ...(inventoryPackage.targetingLogic !== undefined && {
            targeting_logic: inventoryPackage.targetingLogic
        }),
        ...(inventoryPackage.genreSelectionMode !== undefined && {
            genre_selection_mode: inventoryPackage.genreSelectionMode
        })
    };
});
//# sourceMappingURL=inventoryPackage.js.map